import React, { useState, useMemo, useCallback, memo } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useSelector } from "react-redux";

/**
 * PreviousObservation Component
 * 
 * Displays a list of previous feedback/observations with expandable view.
 * Shows first 3 items by default with "View all" / "See less" toggle.
 * 
 * Performance optimized with React.memo and useCallback.
 */
const PreviousObservation = () => {
  const initialState = 3;
  const [noOfElement, setNoOfElement] = useState(initialState);

  // Memoize feedback selector with custom equality check
  const feedBack = useSelector(
    (state) => state?.getFeedback?.getFeedbackDetails?.Previous_Feedback,
    (prev, next) => prev?.length === next?.length
  );

  const feedbackLength = useMemo(() => feedBack?.length || 0, [feedBack]);

  // Memoize sliced feedback to avoid recalculation
  const slicedFeedback = useMemo(() => {
    return feedBack?.slice(0, noOfElement) || [];
  }, [feedBack, noOfElement]);

  const loadMore = useCallback((e) => {
    e.preventDefault();
    setNoOfElement((prev) => (prev === initialState ? feedbackLength : initialState));
  }, [feedbackLength]);

  return (
    <div>
      <div>
        <div className="flex justify-between items-center mx-0">
          <h6 className="flex justify-start text-[#808080] font-medium text-[0.841rem]">
            Previous Observations
          </h6>
          <h6 className="flex justify-end font-medium text-[13.46px] text-[#f27042] whitespace-nowrap">
            {feedbackLength} Observations
          </h6>
        </div>

        <div className="max-h-[193px] overflow-auto">
          {slicedFeedback.map((item, index) => (
            <div
              key={item?.Id || index}
              className="border-l-[3px] border-[#969696] bg-[rgba(150,150,150,0.13)] mb-2 min-h-[1.644rem] flex items-center"
            >
              <div className="leading-tight py-2.5 px-2.5 self-center font-maven font-medium text-[0.6rem] text-[#2b2b2b] h-auto">
                {item?.Observations__c}
              </div>
            </div>
          ))}
        </div>

        {feedbackLength > 3 && (
          <div className="flex justify-end h-[1.6rem] pt-0.5 mx-0">
            <button
              className="bg-white border-none pl-0 flex text-center items-center hover:opacity-80 transition-opacity"
              onClick={loadMore}
            >
              {slicedFeedback.length > 0 && (
                <div className="flex items-center">
                  <p className="font-roboto font-medium text-[0.558rem] text-[#2cc26a] whitespace-nowrap mb-0">
                    {noOfElement === initialState ? "View all" : "See less"}
                  </p>
                  {noOfElement === initialState ? (
                    <ChevronDown className="text-[#2cc26a] h-[0.813rem] w-[0.625rem] ml-1.5 stroke-1" />
                  ) : (
                    <ChevronUp className="text-[#2cc26a] h-[0.813rem] w-[0.625rem] ml-1.5 stroke-1" />
                  )}
                </div>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Memoize component - it only needs to re-render when feedback changes
export default memo(PreviousObservation);
